import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, concatMap, EMPTY, exhaustMap, filter, map, mergeMap, of, switchMap, take } from 'rxjs';

import { GetConfigValuesGQL, SetConfigValuesGQL, ViewDatesGQL } from '../../../../../generated/graphql.generated';
import { dayjs } from '../../../../core/dayjs/dayjs';
import { PermissionService } from '../../../../services/permission.service';
import { TimezoneHelperService } from '../../../../services/timezone-helper.service';
import { brandingFeature } from '../../../../state/branding.store';

import { DispatchCalendarRemarksDialogActions } from './dispatch-calendar-remarks-dialog.actions';
import { dispatchCalendarRemarksDialogFeature } from './dispatch-calendar-remarks-dialog.feature';

export const initDialogDate = createEffect((
    actions$ = inject(Actions),
    timezoneHelper = inject(TimezoneHelperService),
) => {
    return actions$.pipe(
        ofType(DispatchCalendarRemarksDialogActions.dialogOpened),
        map(({ date }) => DispatchCalendarRemarksDialogActions.datesSelected({
            // Callers pass an 'YYYY-MM-DD' wall-date string already resolved
            // in the zone's timezone. When absent, resolve "today" in the
            // zone's timezone (not the browser's local timezone).
            dates: [date ?? dayjs().tz(timezoneHelper.getCurrentTimezone()).format('YYYY-MM-DD')],
        })),
    );
}, { functional: true, dispatch: true });

export const loadViewDates = createEffect((
    actions$ = inject(Actions),
    viewDatesGQL = inject(ViewDatesGQL),
    store = inject(Store),
    permissionHandler = inject(PermissionService),
) => {
    return actions$.pipe(
        ofType(DispatchCalendarRemarksDialogActions.datesSelected),
        // Pair the trigger with the first permission emission; concatLatestFrom
        // drops the action when watchPermission hasn't emitted yet, which would
        // leave the dialog without view dates until the next selection.
        concatMap((action) => permissionHandler.watchPermission('find.viewDates').pipe(
            take(1),
            map((hasViewDates) => [ action, hasViewDates ] as const),
        )),
        filter(([, hasViewDates]) => hasViewDates),
        switchMap(([{ dates }]) => {
            const validDates = dates?.filter((d): d is string => d != null) ?? [];
            if (!validDates.length) { return EMPTY; }
            const orderedDates = [...validDates].sort();
            return of(null).pipe(
                concatLatestFrom(() => store.select(brandingFeature.selectBranding)),
                mergeMap(([_, branding]) => {
                    // An incomplete range selection ([start, null]) means the
                    // user has clicked the first day of a range — load that
                    // day now; the range's second click reloads.
                    const startDate = orderedDates[0];
                    const endDate = orderedDates[orderedDates.length - 1];
                    return viewDatesGQL.fetch(
                        { startDate, endDate, zone: branding?.zoneId },
                        { fetchPolicy: 'network-only' },
                    ).pipe(
                        map((res) => DispatchCalendarRemarksDialogActions.loadViewDatesSuccess({
                            viewDates: res.data?.viewDates ?? [],
                        })),
                    );
                }),
                catchError((error) => of(DispatchCalendarRemarksDialogActions.loadViewDatesError({ error }))),
            );
        }),
    );
}, { functional: true, dispatch: true });

export const saveRemarks = createEffect((
    actions$ = inject(Actions),
    setConfigValuesGQL = inject(SetConfigValuesGQL),
    getConfigValuesGQL = inject(GetConfigValuesGQL),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(DispatchCalendarRemarksDialogActions.saveButtonClicked),
        exhaustMap(() => of(null).pipe(
            concatLatestFrom(() => store.select(dispatchCalendarRemarksDialogFeature.selectPendingRemarks)),
            concatLatestFrom(([_, pendingRemarks]) => store
                .select(dispatchCalendarRemarksDialogFeature.selectDirtyDates)
                .pipe(map((dirtyDates) => [pendingRemarks, dirtyDates] as const))),
            mergeMap(([_, [pendingRemarks, dirtyDates]]) => {
                // The pending list only covers the viewed date range — merge
                // into the stored config so remarks on other dates survive.
                // network-only: a cache-first fetch returns the value from
                // BEFORE the previous save, silently dropping every remark
                // saved earlier in the session.
                return getConfigValuesGQL.fetch(
                    { keys: ['calendarEvents.dateRemarks'] },
                    { fetchPolicy: 'network-only' },
                ).pipe(
                    mergeMap((res) => {
                        let existing: Record<string, unknown> = {};
                        try {
                            const raw = res.data?.getConfigValues?.[0]?.value;
                            existing = raw ? (JSON.parse(raw as string) ?? {}) : {};
                        } catch { existing = {}; }

                        const configValue = { ...existing } as Record<string, { text: string; severity: string }[]>;
                        // Dirty dates take their state entirely from the
                        // pending list — saved remarks the user deleted drop
                        // out here, edits and additions land here. Undirty
                        // dates keep whatever is already stored.
                        for (const date of dirtyDates) {
                            const remarks = pendingRemarks
                                .filter((r) => r.date === date && r.text.trim())
                                .map((r) => ({ text: r.text.trim(), severity: r.severity }));
                            if (remarks.length) {
                                configValue[date] = remarks;
                            } else {
                                delete configValue[date];
                            }
                        }
                        return setConfigValuesGQL.mutate({
                            configs: [{ key: 'calendarEvents.dateRemarks', value: JSON.stringify(configValue) }],
                        }).pipe(
                            map(() => DispatchCalendarRemarksDialogActions.saveRemarksSuccess()),
                        );
                    }),
                );
            }),
            catchError((error) => of(DispatchCalendarRemarksDialogActions.saveRemarksError({ error }))),
        )),
    );
}, { functional: true, dispatch: true });

export const closeDialog = createEffect((
    actions$ = inject(Actions),
) => {
    return actions$.pipe(
        ofType(
            DispatchCalendarRemarksDialogActions.saveRemarksSuccess,
            DispatchCalendarRemarksDialogActions.cancelButtonClicked,
        ),
        map(() => DispatchCalendarRemarksDialogActions.dialogClosed()),
    );
}, { functional: true, dispatch: true });
